import { api, ApiError } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import { navigate } from "../core/router.js";
import { isAuthenticated } from "../core/tokens.js";
import { t } from "../core/i18n.js";
import type { Episode, Title } from "../models.js";

/**
 * Upload / create flow (04-web §2.4, 00 §5.1, 01 §3.B/§3.C/§3.H).
 * 1) POST /v1/titles → Title (draft)
 * 2) POST /v1/titles/:id/episodes → Episode
 * 3) POST /v1/uploads/sign {target:'stream'} → direct upload URL
 * 4) PUT/POST the file bytes straight to Stream (API bypassed, 00 §5.1) with progress
 * 5) ingest the returned stream_uid into the episode
 * AI-content + training/consent gating (00 §7.3) and a deepfake/voice attestation are required.
 */

interface SignResponse {
  target: "r2" | "stream";
  upload_url: string;
  method?: string;
  stream_uid?: string;
  r2_key?: string;
  required_headers?: Record<string, string>;
}

export function uploadView(_params: Record<string, string>, root: HTMLElement): void {
  if (!isAuthenticated()) {
    navigate("/login");
    return;
  }

  const titleInput = h("input", { class: "field", placeholder: t("upload.titlePlaceholder") }) as HTMLInputElement;
  const synopsisInput = h("textarea", { class: "field", placeholder: t("upload.synopsisPlaceholder"), rows: 3 }) as HTMLTextAreaElement;
  const kindSelect = h(
    "select",
    { class: "field" },
    h("option", { value: "drama" }, t("kind.drama")),
    h("option", { value: "movie" }, t("kind.movie")),
  ) as HTMLSelectElement;

  const epNameInput = h("input", { class: "field", placeholder: t("upload.epNamePlaceholder") }) as HTMLInputElement;
  const epIdxInput = h("input", { class: "field", type: "number", value: "1", min: "1" }) as HTMLInputElement;
  const durationInput = h("input", {
    class: "field",
    type: "number",
    placeholder: t("upload.durationPlaceholder"),
    min: "1",
  }) as HTMLInputElement;
  const fileInput = h("input", { class: "field", type: "file", accept: "video/*" }) as HTMLInputElement;

  // Compliance gating (00 §7.3 / 04-web §3).
  const aiGenerated = checkbox();
  const aiEdited = checkbox();
  const deepfake = checkbox();
  const trainingConsent = checkbox();
  const consentAttest = h("textarea", {
    class: "field",
    rows: 2,
    placeholder: t("compliance.consentAttestPlaceholder"),
  }) as HTMLTextAreaElement;

  const status = h("p", { class: "muted" }, "");
  const progressFill = h("div", { class: "progress-fill" }) as HTMLDivElement;
  const progressBar = h("div", { class: "progress-bar", style: "display:none" }, progressFill) as HTMLDivElement;
  const successBox = h("div", { class: "card", style: "padding:14px;margin-top:12px;display:none;cursor:default" }) as HTMLDivElement;
  const submitBtn = h("button", { class: "btn", style: "width:100%;margin-top:12px" }, t("upload.submit")) as HTMLButtonElement;

  // Deepfake attestation row is only relevant when deepfake is checked — highlight it.
  const attestRow = labeled(t("compliance.consentAttestLabel"), consentAttest);
  const syncDeepfake = (): void => {
    attestRow.classList.toggle("required", deepfake.checked);
  };
  deepfake.addEventListener("change", syncDeepfake);

  const setStatus = (text: string, cls = "muted"): void => {
    status.textContent = text;
    status.className = cls;
  };

  const submit = async (): Promise<void> => {
    const titleText = titleInput.value.trim();
    const durationMs = Number(durationInput.value);
    const file = fileInput.files?.[0];

    if (!titleText) return setStatus(t("upload.titleRequired"), "error");
    if (!durationMs || durationMs <= 0) return setStatus(t("upload.durationRequired"), "error");
    if (!file) return setStatus(t("upload.fileRequired"), "error");
    // AI 라벨 미응답 시 게시 차단(00 §7.3): require an explicit answer.
    if (!aiGenerated.checked && !aiEdited.checked && !deepfake.checked) {
      return setStatus(t("compliance.labelRequiredUpload"), "error");
    }
    if (deepfake.checked && !consentAttest.value.trim()) {
      return setStatus(t("compliance.deepfakeConsentRequired"), "error");
    }

    submitBtn.disabled = true;
    successBox.style.display = "none";
    try {
      // 1) Create the draft Title.
      setStatus(t("upload.creatingTitle"));
      const title = await api.post<Title>("/v1/titles", {
        kind: kindSelect.value,
        title: titleText,
        synopsis: synopsisInput.value.trim() || null,
      });

      // 2) Get a direct-upload target (R2 presigned PUT) and stream the bytes there.
      const contentType = file.type || "video/mp4";
      setStatus(t("upload.signing"));
      const sign = await api.post<SignResponse>("/v1/uploads/sign", {
        target: "r2",
        purpose: "episode_media",
        filename: file.name,
        size_bytes: file.size,
        content_type: contentType,
      });

      progressBar.style.display = "block";
      progressFill.style.width = "0%";
      setStatus(t("upload.uploading", { pct: 0 }));
      await uploadWithProgress(
        { ...sign, method: sign.method ?? "PUT", required_headers: { "Content-Type": contentType } },
        file,
        (pct) => {
          progressFill.style.width = `${pct}%`;
          setStatus(t("upload.uploading", { pct }));
        },
      );

      // 3) Create the Episode bound to the uploaded source + AI label / consent (00 §7.3).
      setStatus(t("upload.creatingEpisode"));
      const episode = await api.post<Episode>(`/v1/titles/${title.id}/episodes`, {
        idx: Number(epIdxInput.value) || 1,
        name: epNameInput.value.trim() || null,
        canonical_duration_ms: durationMs,
        source_r2_key: sign.r2_key,
        label: {
          ai_generated: aiGenerated.checked,
          ai_edited: aiEdited.checked,
          deepfake: deepfake.checked,
        },
        training_optout: !trainingConsent.checked,
        consents: buildConsents(deepfake.checked, consentAttest.value.trim()),
      });

      setStatus(t("upload.done"), "muted");
      showSuccess(title.id, episode.id);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
      setStatus(t("upload.failed", { msg }), "error");
    } finally {
      submitBtn.disabled = false;
    }
  };

  const showSuccess = (titleId: string, episodeId: string): void => {
    successBox.replaceChildren(
      h(
        "div",
        { class: "row", style: "display:flex;gap:10px;flex-wrap:wrap" },
        h("button", { class: "btn", onclick: () => navigate(`/edit/${episodeId}`) }, t("upload.openEditor")),
        h("button", { class: "btn secondary", onclick: () => navigate(`/t/${titleId}`) }, t("upload.openTitle")),
      ),
    );
    successBox.style.display = "block";
  };

  submitBtn.addEventListener("click", () => void submit());

  mount(
    root,
    h(
      "section",
      { style: "max-width:560px;margin:0 auto" },
      h("h1", {}, t("upload.title")),
      h("p", { class: "muted" }, t("upload.intro")),
      h("h3", {}, t("upload.sectionTitle")),
      labeled(t("upload.titleField"), titleInput),
      labeled(t("upload.kind"), kindSelect),
      labeled(t("upload.synopsis"), synopsisInput),
      h("h3", {}, t("upload.sectionEpisode")),
      labeled(t("upload.epIdx"), epIdxInput),
      labeled(t("upload.epName"), epNameInput),
      labeled(t("upload.duration"), durationInput),
      labeled(t("upload.file"), fileInput),
      h("h3", {}, t("compliance.sectionTitle")),
      h(
        "div",
        { class: "card", style: "padding:14px;margin:8px 0;cursor:default" },
        wrapCheckbox(aiGenerated, t("compliance.checkGenerated")),
        wrapCheckbox(aiEdited, t("compliance.checkEdited")),
        wrapCheckbox(deepfake, t("compliance.checkDeepfake")),
        h("hr", { style: "border-color:#2a2a32;margin:12px 0" }),
        wrapCheckbox(trainingConsent, t("compliance.trainingConsent")),
        attestRow,
      ),
      submitBtn,
      progressBar,
      status,
      successBox,
    ),
  );
}

/** Direct upload to Stream via XHR so we can surface byte-level progress. */
function uploadWithProgress(sign: SignResponse, file: File, onProgress: (pct: number) => void): Promise<void> {
  const method = (sign.method ?? "POST").toUpperCase();
  const headers: Record<string, string> = { ...(sign.required_headers ?? {}) };
  let body: BodyInit;
  if (method === "POST") {
    const form = new FormData();
    form.append("file", file);
    body = form; // Stream direct upload expects multipart form
  } else {
    body = file;
    if (!headers["Content-Type"]) headers["Content-Type"] = file.type || "video/mp4";
  }

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, sign.upload_url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(t("upload.uploadFailed", { status: xhr.status })));
      }
    });
    xhr.addEventListener("error", () => reject(new Error(t("upload.uploadFailed", { status: 0 }))));
    xhr.send(body);
  });
}

function checkbox(): HTMLInputElement {
  return h("input", { type: "checkbox" }) as HTMLInputElement;
}

function wrapCheckbox(input: HTMLInputElement, label: string): HTMLElement {
  return h(
    "label",
    { style: "display:flex;gap:8px;align-items:flex-start;margin:8px 0;font-size:14px" },
    input,
    h("span", {}, label),
  );
}

function labeled(label: string, field: HTMLElement): HTMLElement {
  return h("label", { class: "labeled", style: "display:block;margin:8px 0" }, h("span", { class: "muted", style: "font-size:13px" }, label), field);
}

function buildConsents(deepfake: boolean, attestation: string): Array<Record<string, unknown>> {
  if (!deepfake || !attestation) return [];
  return [
    {
      scope: "likeness",
      granted: true,
      legal_basis: "tos_license",
      is_special_category: true,
      region: "KR",
      evidence_ref: attestation,
    },
  ];
}
